import React, { useState, useEffect } from 'react';
import { 
  Heart, 
  Clock, 
  Sun, 
  Moon, 
  Star,
  Sparkle,
  DropHalfBottom,
  Camera,
  Flask,
  TrendUp,
  Calendar,
  Target,
  CheckCircle,
  Circle,
  ArrowRight,
  Play,
  BookOpen,
  Article,
  User,
  Fire,
  Trophy,
  ChartLine,
  Eye,
  Shield,
  Lightbulb,
  Bell,
  Plus
} from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { workerFetch } from '../utils/apiUtils';
import RoutineTimeModal from './RoutineTimeModal';
import SkinAnalytics from './SkinAnalytics';

const WORKER_URL = 'https://empty-rain-2654.er-hooman.workers.dev';

const SkinProfile = ({ userProfile, processedAnalysis, onNavigateToAnalysis }) => {
  const navigate = useNavigate();
  const [skinData, setSkinData] = useState(null);
  const [currentRoutine, setCurrentRoutine] = useState(null);
  const [recentProducts, setRecentProducts] = useState([]);
  const [userStats, setUserStats] = useState(null);
  const [articles, setArticles] = useState([]);
  const [skinProgress, setSkinProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showTimeModal, setShowTimeModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);

  // Check if user profile is complete (only age and skinType are required)
  const isProfileComplete = !!(userProfile && userProfile.age && userProfile.skinType);
  
  // Debug profile check
  console.log('🔍 SkinProfile check:', {
    hasUserProfile: !!userProfile,
    age: userProfile?.age,
    skinType: userProfile?.skinType,
    isProfileComplete,
  });
  
  // Only redirect to onboarding if we have userProfile data but it's incomplete
  const shouldRedirectToOnboarding = userProfile && !isProfileComplete;

  useEffect(() => {
    fetchSkinData();
  }, []);

  const fetchSkinData = async () => {
    try {
      const token = localStorage.getItem('firebaseIdToken');
      if (!token) return;

      const [skinRes, routineRes, productsRes, statsRes, articlesRes, progressRes] = await Promise.all([
        workerFetch(`${WORKER_URL}/api/skin/profile`, { method: 'GET', headers: { 'Authorization': `Bearer ${token}` } }),
        workerFetch(`${WORKER_URL}/api/routine/current`, { method: 'GET', headers: { 'Authorization': `Bearer ${token}` } }),
        workerFetch(`${WORKER_URL}/api/products/recent`, { method: 'GET', headers: { 'Authorization': `Bearer ${token}` } }),
        workerFetch(`${WORKER_URL}/api/user/stats`, { method: 'GET', headers: { 'Authorization': `Bearer ${token}` } }),
        workerFetch(`${WORKER_URL}/api/articles/recommended`, { method: 'GET', headers: { 'Authorization': `Bearer ${token}` } }),
        workerFetch(`${WORKER_URL}/api/skin/progress`, { method: 'GET', headers: { 'Authorization': `Bearer ${token}` } })
      ]);

      if (skinRes.ok) {
        const data = await skinRes.json();
        setSkinData(data);
      }

      if (progressRes.ok) {
        const data = await progressRes.json();
        console.log('📊 Skin progress data:', data);
        setSkinData(data); // Use progress data instead of profile data
      }

      if (routineRes.ok) {
        const data = await routineRes.json();
        setCurrentRoutine(data);
      }

      if (productsRes.ok) {
        const data = await productsRes.json();
        setRecentProducts(data.products || []);
      }

      if (statsRes.ok) {
        const data = await statsRes.json();
        setUserStats(data);
      }

      if (articlesRes.ok) {
        const data = await articlesRes.json();
        setArticles(data.articles || []);
      }

      if (progressRes.ok) {
        const data = await progressRes.json();
        setSkinProgress(data);
      }
    } catch (error) {
      console.error('Error fetching skin data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getCurrentTimeOfDay = () => {
    const hour = new Date().getHours();
    if (hour >= 5 && hour < 12) return 'morning';
    if (hour >= 12 && hour < 17) return 'afternoon';
    if (hour >= 17 && hour < 21) return 'evening';
    return 'night';
  };


  const parseFirestoreValue = (value) => {
    if (!value) return null;
    if (typeof value === 'object') {
      if (value.stringValue) return value.stringValue;
      if (value.integerValue) return parseInt(value.integerValue);
      if (value.doubleValue) return parseFloat(value.doubleValue);
      if (value.booleanValue) return value.booleanValue;
      if (value.timestampValue) return value.timestampValue;
    }
    return value;
  };


  const getSkinProgress = () => {
    // Return hardcoded values from latest analysis for now
    return {
      hydration: 50,  // From latest analysis
      clarity: 55,    // From latest analysis  
      texture: 40     // From latest analysis
    };
  };

  const progress = getSkinProgress();

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-50 via-white to-purple-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-500"></div>
      </div>
    );
  }

  // If profile is not complete, redirect to onboarding
  if (shouldRedirectToOnboarding) {
    console.log('Profile not complete, redirecting to onboarding');
    navigate('/onboarding');
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-white to-purple-50" dir="rtl">
      {/* Header */}
      <div className="bg-gradient-to-r from-pink-500 to-purple-600 text-white p-6 rounded-b-3xl shadow-lg">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold">سلام {userProfile?.name || 'عزیز'}!</h1>
            <p className="text-pink-100 text-sm">امروز چطور می‌خواهید از پوست خود مراقبت کنید؟</p>
          </div>
          <div className="text-right">
            <p className="text-xs text-pink-200">
              {new Date().toLocaleDateString('fa-IR')}
            </p>
          </div>
        </div>

        {/* User Stats */}
        {userStats && (
          <div className="grid grid-cols-3 gap-4 mt-4">
            <div className="bg-white/20 backdrop-blur-sm rounded-xl p-3 text-center">
              <Fire size={24} className="mx-auto mb-1 text-yellow-300" />
              <p className="text-lg font-bold">{userStats.streakDays || 0}</p>
              <p className="text-xs text-pink-100">روز متوالی</p>
            </div>
            <div className="bg-white/20 backdrop-blur-sm rounded-xl p-3 text-center">
              <Camera size={24} className="mx-auto mb-1 text-blue-300" />
              <p className="text-lg font-bold">{userStats.totalAnalyses || 0}</p>
              <p className="text-xs text-pink-100">آنالیز</p>
            </div>
            <div className="bg-white/20 backdrop-blur-sm rounded-xl p-3 text-center">
              <Trophy size={24} className="mx-auto mb-1 text-yellow-300" />
              <p className="text-lg font-bold">{userStats.level || 1}</p>
              <p className="text-xs text-pink-100">سطح</p>
            </div>
          </div>
        )}
      </div>

      <div className="p-6 pb-24 space-y-6">

        {/* Latest Analysis Score Card */}
        {processedAnalysis && (
          <div 
            className="bg-gradient-to-br from-pink-500 to-purple-600 rounded-2xl p-6 shadow-2xl cursor-pointer hover:shadow-3xl transition-all duration-300 transform hover:scale-[1.02]"
            onClick={() => onNavigateToAnalysis && onNavigateToAnalysis(processedAnalysis.analysisId)}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Camera size={24} className="text-white" />
                <h2 className="text-white font-bold text-lg">آخرین آنالیز پوست</h2>
              </div>
              <ArrowRight size={20} className="text-white" />
            </div>
            
            <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 mb-3">
              <div className="flex items-center justify-center mb-2">
                <div className="text-5xl font-bold text-white">{processedAnalysis.score}</div>
                <div className="text-white/80 text-lg mr-2">/100</div>
              </div>
              <div className="text-center text-white/90 text-sm">
                {processedAnalysis.score >= 70 ? 'عالی' : processedAnalysis.score >= 50 ? 'خوب' : 'نیاز به بهبود'}
              </div>
            </div>

            {processedAnalysis.explanation && (
              <p className="text-white/90 text-sm leading-relaxed line-clamp-2">
                {processedAnalysis.explanation}
              </p>
            )}

            {processedAnalysis.date && (
              <div className="mt-3 flex items-center gap-2 text-white/70 text-xs">
                <Calendar size={14} />
                <span>{new Date(processedAnalysis.date).toLocaleDateString('fa-IR')}</span>
              </div>
            )}
          </div>
        )}

        {/* Recent Products */}
        {recentProducts.length > 0 && (
          <div className="bg-white rounded-2xl p-6 shadow-lg border border-pink-100">
            <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
              <Flask size={20} className="text-purple-500" />
              محصولات اخیر
            </h2>
            
            <div className="space-y-3">
              {recentProducts.slice(0, 3).map((product, index) => (
                <div key={index} className="flex items-center gap-3 p-3 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl">
                  <div className="w-10 h-10 bg-gradient-to-br from-purple-100 to-pink-100 rounded-full flex items-center justify-center">
                    <Flask size={16} className="text-purple-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-800 text-sm">{product.name || 'محصول'}</h3>
                    <p className="text-xs text-gray-600">{product.category || 'مراقبت'}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-gray-500">{product.lastUsed || 'امروز'}</p>
                    <div className="flex items-center gap-1 mt-1">
                      <Star size={12} className="text-yellow-500" />
                      <span className="text-xs text-gray-600">{product.rating || 4.5}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Articles */}
        {articles.length > 0 && (
          <div className="bg-white rounded-2xl p-6 shadow-lg border border-pink-100">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                <BookOpen size={20} className="text-blue-500" />
                مقالات پیشنهادی
              </h2>
              <button className="text-pink-500 text-sm font-medium">مشاهده همه</button>
            </div>
            
            <div className="space-y-3">
              {articles.slice(0, 2).map((article, index) => (
                <div key={index} className="flex items-center gap-3 p-3 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl hover:shadow-md transition-shadow">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-full flex items-center justify-center">
                    <Article size={20} className="text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-800 text-sm mb-1">{article.title || 'مقاله'}</h3>
                    <p className="text-xs text-gray-600 mb-2">{article.excerpt || 'خلاصه مقاله'}</p>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-blue-600 bg-blue-100 px-2 py-1 rounded-full">{article.category || 'مراقبت'}</span>
                      <span className="text-xs text-gray-500">{article.readTime || 5} دقیقه مطالعه</span>
                    </div>
                  </div>
                  <ArrowRight size={16} className="text-gray-400" />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Quick Actions */}
        <div className="grid grid-cols-3 gap-4">
          <button 
            onClick={() => navigate('/scanpage')}
            className="bg-gradient-to-br from-pink-500 to-pink-600 text-white p-4 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 flex flex-col items-center justify-center min-h-[120px]"
          >
            <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mb-3">
              <Camera size={24} className="text-white" />
            </div>
            <h3 className="font-bold text-base mb-1">اسکن پوست</h3>
            <p className="text-pink-100 text-xs text-center">آنالیز فوری</p>
          </button>

          <button 
            onClick={() => navigate('/routine')}
            className="bg-gradient-to-br from-purple-500 to-purple-600 text-white p-4 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 flex flex-col items-center justify-center min-h-[120px]"
          >
            <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mb-3">
              <DropHalfBottom size={24} className="text-white" />
            </div>
            <h3 className="font-bold text-base mb-1">روتین من</h3>
            <p className="text-purple-100 text-xs text-center">مراقبت روزانه</p>
          </button>

          <button 
            onClick={() => navigate('/profitscan')}
            className="bg-gradient-to-br from-green-500 to-green-600 text-white p-4 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 flex flex-col items-center justify-center min-h-[120px]"
          >
            <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mb-3">
              <Flask size={24} className="text-white" />
            </div>
            <h3 className="font-bold text-base mb-1">اسکن محصول</h3>
            <p className="text-green-100 text-xs text-center">بررسی محصولات</p>
          </button>
        </div>

        {/* Skin Tips */}
        <div className="bg-gradient-to-r from-pink-50 to-purple-50 rounded-2xl p-6 border border-pink-200">
          <h2 className="text-lg font-bold text-gray-800 mb-3 flex items-center gap-2">
            <Sparkle size={20} className="text-pink-500" />
            نکته پوستی امروز
          </h2>
          <p className="text-gray-700 leading-relaxed text-sm">
            برای حفظ سلامت پوست، حتماً از کرم مرطوب‌کننده استفاده کنید. 
            این کار به حفظ رطوبت پوست و جلوگیری از خشکی کمک می‌کند.
          </p>
        </div>

        {/* Skin Analytics */}
        <SkinAnalytics latestAnalysis={processedAnalysis} />
      </div>

      {/* Routine Time Modal */}
      <RoutineTimeModal
        isOpen={showTimeModal}
        onClose={() => setShowTimeModal(false)}
        productName={selectedProduct?.name}
        timeOfDay={selectedProduct?.timeOfDay}
      />
    </div>
  );
};

export default SkinProfile;
